import React, { useEffect } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { LayoutDashboard, ExternalLink, Palette } from 'lucide-react';
import { ADMIN_NEXT_BASE, ADMIN_TOOL_GROUPS, adminToolPath } from './registry';

const navLinkClasses = ({ isActive }: { isActive: boolean }): string =>
  [
    'flex items-center gap-2 rounded-lg px-3 py-2 text-sm whitespace-nowrap transition-colors',
    isActive
      ? 'bg-primary-50 text-primary-700 font-medium dark:bg-primary-900 dark:bg-opacity-30 dark:text-primary-300'
      : 'text-text-secondary hover:bg-background-tertiary hover:text-text-primary dark:text-text-dark-secondary dark:hover:bg-background-dark-tertiary dark:hover:text-text-dark-primary',
  ].join(' ');

/**
 * Shell for the new admin suite. Renders a persistent tool navigation (built
 * from the registry) beside the active tool, so every tool is one click from
 * every other tool. On small screens the rail collapses to a horizontally
 * scrollable strip above the content.
 */
const AdminNextLayout: React.FC = () => {
  /**
   * Admin-next never falls back to a mobile view.
   *
   * index.css hides every table element below 1024px app-wide and swaps in a
   * "use card view" banner. That is right for the pro and citizen surfaces,
   * which have card views; admin-next does not, so the rule leaves an admin
   * tool with no data on screen at all in a narrow window. This flag scopes
   * those rules out (see `body:not(.admin-next-desktop)` in index.css) and is
   * tied to the layout's lifetime, so it applies to admin-next and nothing else.
   */
  useEffect(() => {
    document.body.classList.add('admin-next-desktop');
    return () => document.body.classList.remove('admin-next-desktop');
  }, []);

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      <nav
        aria-label="Admin tools"
        className="flex shrink-0 gap-1 overflow-x-auto pb-2 lg:w-52 lg:flex-col lg:overflow-visible lg:pb-0"
      >
        <NavLink to={ADMIN_NEXT_BASE} end className={navLinkClasses}>
          <LayoutDashboard className="h-4 w-4 shrink-0" />
          Dashboard
        </NavLink>

        {ADMIN_TOOL_GROUPS.map((group) => (
          <React.Fragment key={group.key}>
            <div className="hidden pb-1 pt-4 pl-3 text-xs font-semibold uppercase tracking-wide text-text-tertiary dark:text-text-dark-tertiary lg:block">
              {group.title}
            </div>
            {group.tools.map((tool) => (
              <NavLink key={tool.key} to={adminToolPath(tool)} className={navLinkClasses}>
                <tool.icon className="h-4 w-4 shrink-0" />
                <span className="flex-1">{tool.title}</span>
                {tool.status === 'legacy' && (
                  <ExternalLink
                    className="h-3 w-3 shrink-0 text-text-tertiary dark:text-text-dark-tertiary"
                    aria-label="Links to legacy tool"
                  />
                )}
              </NavLink>
            ))}
          </React.Fragment>
        ))}

        {/*
         * Reference, not a tool — which is why it is here rather than in the
         * registry. Every registry entry is a route segment under
         * ADMIN_NEXT_BASE (see `adminToolPath`), and /design-system sits
         * outside that tree; adding it there would either mint a broken path
         * or fall through to the LegacyToolPage catch-all.
         *
         * It is read-only by nature: the living style guide renders every
         * shared component and changes nothing. It has the same guard as the
         * admin tools (requireAdmin), and until now nothing in the UI linked
         * to it at all — the conventions docs cite it six times and you had to
         * already know the URL.
         */}
        <div className="hidden pb-1 pt-4 pl-3 text-xs font-semibold uppercase tracking-wide text-text-tertiary dark:text-text-dark-tertiary lg:block">
          Reference
        </div>
        <NavLink to="/design-system" className={navLinkClasses}>
          <Palette className="h-4 w-4 shrink-0" />
          <span className="flex-1">Design System</span>
          <ExternalLink
            className="h-3 w-3 shrink-0 text-text-tertiary dark:text-text-dark-tertiary"
            aria-label="Leaves the admin tools"
          />
        </NavLink>
      </nav>

      <div className="min-w-0 flex-1">
        <Outlet />
      </div>
    </div>
  );
};

export default AdminNextLayout;
